import { useQuery } from '@tanstack/react-query';
import { AppWindow, ChevronRight, ExternalLink } from 'lucide-react';
import React, { useState } from 'react';

import { ExternalApp, externalappsListApps } from '@column/api';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  StatusIndicator,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@column/ui';

import { ExternalAppMode, getExternalAppURL, getTargetMode } from '~/lib/apps/utils';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useSessionStore } from '~/stores/Session';
import { setAppsContextCookies } from '~/util';

const AppModeIndicator: React.FC<{ className?: string; mode: ExternalAppMode }> = ({ className, mode }) => {
  return (
    <TooltipProvider>
      <span className={cn('flex shrink-0 items-center gap-1', className)}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <StatusIndicator mode={mode} size="sm" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6}>
            This app will open in {mode} mode
          </TooltipContent>
        </Tooltip>
      </span>
    </TooltipProvider>
  );
};

export const NavApps: React.FC = () => {
  const { currentPlatform, currentUser, isSandbox, setSandbox } = useSessionStore();
  const isExternalAppsEnabled = useFeatureFlag(FeatureFlag.EnableExternalApps);
  const [isOpen, setIsOpen] = useState(false);

  const appsQuery = useQuery({
    queryKey: ['externalApps', currentPlatform?.id],
    queryFn: async () => {
      const { data } = await externalappsListApps({ throwOnError: true });
      return data?.apps ?? [];
    },
    enabled: isExternalAppsEnabled && !!currentPlatform?.id,
  });

  const apps = appsQuery.data ?? [];

  const handleAppClick = (app: ExternalApp) => {
    const targetMode = getTargetMode(app.modes, isSandbox);
    const appURL = getExternalAppURL(app);
    const platformId = currentPlatform?.id ?? currentUser?.defaultPlatformId;

    if (!targetMode || !appURL || !platformId) {
      return;
    }

    const targetSandbox = targetMode === 'sandbox';
    if (targetSandbox !== isSandbox) {
      setSandbox(targetSandbox);
    }

    setAppsContextCookies({ platformId, isSandbox: targetSandbox });
    window.location.assign(appURL);
  };

  if (!isExternalAppsEnabled || apps.length === 0) {
    return null;
  }

  return (
    <Collapsible asChild open={isOpen} onOpenChange={setIsOpen}>
      <SidebarMenuItem className="py-0.5">
        <div className="group/apps-trigger relative">
          <CollapsibleTrigger asChild>
            <SidebarMenuButton className="bg-transparent! transition-[color]! before:absolute before:inset-0 before:-z-[1] before:rounded-md before:bg-sidebar-accent before:opacity-0 before:transition-opacity before:duration-150 before:pointer-events-none before:content-[''] after:absolute after:inset-0 after:-z-[2] after:rounded-md after:bg-gray-100 dark:after:bg-gray-800 after:opacity-0 after:transition-opacity after:duration-150 after:pointer-events-none after:content-[''] group-hover/apps-trigger:after:opacity-100 hover:[&>span]:text-gray-900 dark:hover:[&>span]:text-sidebar-foreground hover:[&_svg]:[--icon-color:var(--color-gray-700)]! dark:hover:[&_svg]:[--icon-color:var(--color-sidebar-foreground)]! [&>span]:transition-colors [&>span]:duration-150 [&_svg_path]:transition-[stroke,fill] [&_svg_path]:duration-150">
              <AppWindow className="m-0.5 text-gray-500" />
              <span>Apps</span>
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleTrigger asChild>
            <SidebarMenuAction className="peer-data-[size=default]/menu-button:top-2 [&>svg]:transition-transform [&>svg]:duration-200 [&>svg]:ease-out data-[state=open]:[&>svg]:rotate-90">
              <ChevronRight className="size-4" />
              <span className="sr-only">Toggle Apps</span>
            </SidebarMenuAction>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent>
          <SidebarMenuSub className="gap-0">
            {apps.map((app) => {
              const targetMode = getTargetMode(app.modes, isSandbox);
              const disabled = !targetMode || !getExternalAppURL(app);

              return (
                <SidebarMenuSubItem key={app.id} className="py-0.5">
                  <SidebarMenuSubButton
                    asChild
                    className="h-auto min-h-7 w-full justify-between bg-transparent! py-1.5 transition-[color]! before:absolute before:inset-0 before:-z-[1] before:rounded-md before:bg-sidebar-accent before:opacity-0 before:transition-opacity before:duration-150 before:pointer-events-none before:content-[''] after:absolute after:inset-0 after:-z-[2] after:rounded-md after:bg-gray-100 dark:after:bg-gray-800 after:opacity-0 after:transition-opacity after:duration-150 after:pointer-events-none after:content-[''] group-hover/menu-sub-item:after:opacity-100 hover:[&>span:first-child]:text-gray-900 dark:hover:[&>span:first-child]:text-sidebar-foreground hover:[&_svg]:[--icon-color:var(--color-gray-700)]! dark:hover:[&_svg]:[--icon-color:var(--color-sidebar-foreground)]! [&>span:first-child]:min-w-0 [&>span:first-child]:truncate [&>span:first-child]:transition-colors [&>span:first-child]:duration-150 [&>span:last-child]:overflow-visible [&>span:last-child]:whitespace-nowrap [&_svg_path]:transition-[stroke,fill] [&_svg_path]:duration-150"
                  >
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => handleAppClick(app)}
                      className={cn(
                        'group/apps-subitem w-full cursor-pointer',
                        disabled && 'cursor-not-allowed opacity-50'
                      )}
                    >
                      <span>{app.display_name}</span>
                      <span className="ml-auto flex shrink-0 items-center gap-2">
                        {targetMode && (
                          <AppModeIndicator
                            mode={targetMode}
                            className="opacity-0 transition-opacity duration-150 group-hover/apps-subitem:opacity-100"
                          />
                        )}
                        <ExternalLink className="size-3.5 shrink-0 text-gray-400 opacity-0 transition-opacity duration-150 group-hover/apps-subitem:opacity-100" />
                      </span>
                    </button>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              );
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
};
